import { useCallback, useContext } from 'react'

import { type BasketData, type BasketItem, type PromotionCode, BasketContext } from '@/context/basketContext'

const deduplicateItems = (items: BasketItem[]): BasketItem[] =>
  Object.values(
    items.reduce<Record<string, BasketItem>>((acc, item) => {
      const key = item.priceLookupKey
      acc[key] = {
        priceLookupKey: key,
        quantity: (acc[key]?.quantity ?? 0) + (item.quantity ?? 1),
      }
      return acc
    }, {}),
  )

export const useBasketActions = () => {
  const context = useContext(BasketContext)
  if (context === undefined) {
    throw new Error('useBasketActions must be used within a BasketProvider')
  }

  const { setBasketData } = context

  const updateBasketData = useCallback(
    (data: Partial<BasketData>) => {
      setBasketData((prev) => ({ ...prev, ...data }))
    },
    [setBasketData],
  )

  const addItem = useCallback(
    (priceLookupKey: string) => {
      setBasketData((prev) => {
        const existing = prev.items.find((item) => item.priceLookupKey === priceLookupKey)
        if (existing) {
          return {
            ...prev,
            items: prev.items.map((item) =>
              item.priceLookupKey === priceLookupKey ? { ...item, quantity: (item.quantity ?? 1) + 1 } : item,
            ),
          }
        }
        return { ...prev, items: [...prev.items, { priceLookupKey }] }
      })
    },
    [setBasketData],
  )

  const removeItem = useCallback(
    (priceLookupKey: string) => {
      setBasketData((prev) => {
        const existing = prev.items.find((item) => item.priceLookupKey === priceLookupKey)
        if (!existing) return prev
        const currentQuantity = existing.quantity ?? 1
        if (currentQuantity <= 1) {
          return { ...prev, items: prev.items.filter((item) => item.priceLookupKey !== priceLookupKey) }
        }
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.priceLookupKey === priceLookupKey ? { ...item, quantity: currentQuantity - 1 } : item,
          ),
        }
      })
    },
    [setBasketData],
  )

  const clearItems = useCallback(() => {
    setBasketData((prev) => ({
      ...prev,
      items: [],
    }))
  }, [setBasketData])

  const setItems = useCallback(
    (items: BasketItem[]) => {
      setBasketData((prev) => {
        const nextItems = deduplicateItems(items)
        // Keeps the same array when nothing changed: a new one makes the checkout create a new session.
        return JSON.stringify(nextItems) === JSON.stringify(prev.items) ? prev : { ...prev, items: nextItems }
      })
    },
    [setBasketData],
  )

  const setPromotionCode = useCallback(
    (promotionCode: PromotionCode | null) => {
      setBasketData((prev) => ({ ...prev, promotionCode }))
    },
    [setBasketData],
  )

  const clearBasket = useCallback(() => {
    setBasketData({
      items: [],
      promotionCode: null,
    })
  }, [setBasketData])

  return {
    updateBasketData,
    addItem,
    removeItem,
    clearItems,
    setItems,
    setPromotionCode,
    clearBasket,
  }
}
